import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Button, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, Select, MenuItem, Snackbar, Alert, Chip, IconButton, Tooltip,
  InputAdornment, TableSortLabel
} from "@mui/material";
import {
  Edit, Delete, Add, Logout, FileDownload, Category, Search
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_API_URL;
const FIRMS = ["Datachef", "Techsahyogi"];

const STATUS_COLOR = {
  "New": "info",
  "In-Progress": "warning",
  "Completed": "success",
  "On-Hold": "secondary",
  "Cancelled": "error",
};

const AdminDashboard = () => {
  const [jobs, setJobs] = useState([]);
  const [editJob, setEditJob] = useState(null);
  const [invoiceFields, setInvoiceFields] = useState({ invoice_raised: "", invoice_amount: "", payment_received: "", status: "", type_of_job: "", job_id: "", job_received_date: "", client_name: "", job_description: "" });
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [typeOptions, setTypeOptions] = useState([]);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [selectedFirm, setSelectedFirm] = useState("Datachef");
  const navigate = useNavigate();

  const statusOptions = ["New", "In-Progress", "Completed", "On-Hold", "Cancelled"];

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/");
      return;
    }
    fetchJobs();
    fetchTypeOptions();
    // eslint-disable-next-line
  }, []);

  const fetchJobs = async () => {
    const res = await axios.get(`${API}/api/jobs`);
    setJobs(res.data);
  };

  const fetchTypeOptions = async () => {
    try {
      const res = await axios.get(`${API}/api/codes`);
      setTypeOptions(res.data);
    } catch {
      setTypeOptions([]);
    }
  };

  const handleEdit = (job) => {
    setEditJob(job);
    setInvoiceFields({
      invoice_raised: job.invoice_raised ?? 0,
      invoice_amount: job.invoice_amount ?? 0,
      payment_received: job.payment_received ?? 0,
      status: job.status ?? "New",
      type_of_job: job.type_of_job ?? "",
      job_id: job.job_id ?? "",
      job_received_date: job.job_received_date ? job.job_received_date.slice(0, 10) : "",
      client_name: job.client_name ?? "",
      job_description: job.job_description ?? ""
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        invoice_raised: Number(invoiceFields.invoice_raised),
        invoice_amount: Number(invoiceFields.invoice_amount),
        payment_received: Number(invoiceFields.payment_received),
        status: invoiceFields.status || "New",
        type_of_job: invoiceFields.type_of_job,
        job_id: invoiceFields.job_id,
        job_received_date: invoiceFields.job_received_date,
        client_name: invoiceFields.client_name,
        job_description: invoiceFields.job_description
      };
      await axios.put(`${API}/api/jobs/${editJob.id}`, payload);
      setUpdateSuccess(true);
      setTimeout(() => setUpdateSuccess(false), 3000);
      setEditJob(null);
      await fetchJobs();
    } catch (err) {
      alert("Failed to update job");
    }
  };

  const handleExport = () => window.open(`${API}/api/jobs/export`, "_blank");
  const handleCreateJob = () => navigate("/user/new", { state: { user: { role: "admin" }, firm: selectedFirm } });
  const handleLogout = () => { localStorage.removeItem("admin_token"); navigate("/"); };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Are you sure you want to delete this job?')) return;
    try {
      await axios.delete(`${API}/api/jobs/${jobId}`);
      fetchJobs();
    } catch (err) {
      alert('Failed to delete job');
    }
  };

  const normalizeStatus = s => (s || '').toLowerCase().replace(/[\s_]+/g, '-');
  const filteredJobs = jobs.filter(job =>
    ((job.firm || 'Datachef') === selectedFirm) &&
    (job.client_name?.toLowerCase().includes(search.toLowerCase()) ||
      job.job_id?.toLowerCase().includes(search.toLowerCase())) &&
    (status ? normalizeStatus(job.status) === normalizeStatus(status) : true)
  );

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortDirection('asc');
    }
  };

  const sortedJobs = [...filteredJobs].sort((a, b) => {
    let aValue = a[sortBy], bValue = b[sortBy];
    if (aValue === undefined || aValue === null) aValue = '';
    if (bValue === undefined || bValue === null) bValue = '';
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
  const boolChip = v => <Chip size="small" variant="outlined" label={v ? "Yes" : "No"} color={v ? "success" : "default"} />;

  const columns = [
    { label: 'Job ID', key: 'job_id' },
    { label: 'Received', key: 'job_received_date' },
    { label: 'Client', key: 'client_name' },
    { label: 'Description', key: 'job_description' },
    { label: 'Assigned To', key: 'assigned_to' },
    { label: 'Type of Job', key: 'type_of_job' },
    { label: 'Status', key: 'status' },
    { label: 'Invoice', key: 'invoice_raised' },
    { label: 'Amount', key: 'invoice_amount' },
    { label: 'Payment', key: 'payment_received' },
    { label: 'Actions', key: null },
  ];

  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, md: 4 } }}>
      {/* Header */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Admin Dashboard</Typography>
        <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
          <Select
            value={selectedFirm}
            onChange={e => setSelectedFirm(e.target.value)}
            size="small"
            sx={{ bgcolor: '#fff', borderRadius: 1, minWidth: 150 }}
          >
            {FIRMS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
          </Select>
          <Button variant="contained" color="inherit" sx={{ color: 'primary.main', bgcolor: '#fff' }} startIcon={<Category />} onClick={() => navigate('/admin/masters')}>Masters</Button>
          <Button variant="contained" color="inherit" sx={{ color: 'primary.main', bgcolor: '#fff' }} startIcon={<Add />} onClick={handleCreateJob}>Create Job</Button>
          <Button variant="outlined" color="inherit" sx={{ borderColor: 'rgba(255,255,255,0.6)' }} startIcon={<Logout />} onClick={handleLogout}>Logout</Button>
        </Box>
      </Paper>

      {/* Toolbar */}
      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          placeholder="Search by Client or Job ID"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        <Select value={status} onChange={e => setStatus(e.target.value)} displayEmpty size="small" sx={{ minWidth: 150 }}>
          <MenuItem value="">All Statuses</MenuItem>
          {statusOptions.map((o, i) => <MenuItem key={i} value={o}>{o.replace(/-/g, ' ')}</MenuItem>)}
        </Select>
        <Typography variant="body2" color="text.secondary">{sortedJobs.length} job{sortedJobs.length !== 1 ? 's' : ''}</Typography>
        <Button variant="outlined" startIcon={<FileDownload />} onClick={handleExport} sx={{ ml: 'auto' }}>Export to Excel</Button>
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '68vh' }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell
                    key={col.key || col.label}
                    sx={{ fontWeight: 700, bgcolor: 'grey.100', whiteSpace: 'nowrap' }}
                    align={col.key === null ? 'right' : 'left'}
                  >
                    {col.key ? (
                      <TableSortLabel
                        active={sortBy === col.key}
                        direction={sortBy === col.key ? sortDirection : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedJobs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                    No jobs found for {selectedFirm}.
                  </TableCell>
                </TableRow>
              )}
              {sortedJobs.map(job => (
                <TableRow key={job.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{job.job_id}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(job.job_received_date)}</TableCell>
                  <TableCell sx={{ maxWidth: 160 }}>{job.client_name}</TableCell>
                  <TableCell sx={{ maxWidth: 260 }}>
                    <Tooltip title={job.job_description || ''}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.job_description}</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{job.assigned_to || '—'}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Tooltip title={job.type_of_job || ''}>
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.type_of_job}</Box>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={(job.status || '').replace(/-/g, ' ')} color={STATUS_COLOR[job.status] || 'default'} />
                  </TableCell>
                  <TableCell>{boolChip(job.invoice_raised)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{job.invoice_amount ? `₹${Number(job.invoice_amount).toLocaleString('en-IN')}` : '—'}</TableCell>
                  <TableCell>{boolChip(job.payment_received)}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Edit"><IconButton size="small" color="primary" onClick={() => handleEdit(job)}><Edit fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDelete(job.id)}><Delete fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Edit Modal */}
      <Dialog open={!!editJob} onClose={() => setEditJob(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Job {editJob?.job_id}</DialogTitle>
        <DialogContent>
          <TextField label="Client Name" value={invoiceFields.client_name} onChange={e => setInvoiceFields(f => ({ ...f, client_name: e.target.value }))} fullWidth margin="normal" />
          <TextField label="Job Description" value={invoiceFields.job_description} onChange={e => setInvoiceFields(f => ({ ...f, job_description: e.target.value }))} fullWidth margin="normal" multiline rows={3} />
          <TextField label="Job ID" value={invoiceFields.job_id} onChange={e => setInvoiceFields(f => ({ ...f, job_id: e.target.value }))} fullWidth margin="normal" />
          <TextField label="Received Date" type="date" value={invoiceFields.job_received_date} onChange={e => setInvoiceFields(f => ({ ...f, job_received_date: e.target.value }))} fullWidth margin="normal" InputLabelProps={{ shrink: true }} />
          <TextField label="Invoice Raised" select value={invoiceFields.invoice_raised} onChange={e => setInvoiceFields(f => ({ ...f, invoice_raised: e.target.value }))} fullWidth margin="normal">
            <MenuItem value={1}>Yes</MenuItem>
            <MenuItem value={0}>No</MenuItem>
          </TextField>
          <TextField label="Invoice Amount" type="number" value={invoiceFields.invoice_amount} onChange={e => setInvoiceFields(f => ({ ...f, invoice_amount: e.target.value }))} fullWidth margin="normal" />
          <TextField label="Payment Received" select value={invoiceFields.payment_received} onChange={e => setInvoiceFields(f => ({ ...f, payment_received: e.target.value }))} fullWidth margin="normal">
            <MenuItem value={1}>Yes</MenuItem>
            <MenuItem value={0}>No</MenuItem>
          </TextField>
          <TextField label="Type of Job" select value={invoiceFields.type_of_job} onChange={e => setInvoiceFields(f => ({ ...f, type_of_job: e.target.value }))} fullWidth margin="normal">
            <MenuItem value="">Select Type of Job</MenuItem>
            {typeOptions.map((option, idx) => (
              <MenuItem key={idx} value={option.label || option.item || option.type_of_job}>
                {option.label || option.item || option.type_of_job}
              </MenuItem>
            ))}
          </TextField>
          <TextField label="Status" select value={invoiceFields.status} onChange={e => setInvoiceFields(f => ({ ...f, status: e.target.value }))} fullWidth margin="normal">
            {statusOptions.map((option, idx) => <MenuItem key={idx} value={option}>{option.replace(/-/g, ' ')}</MenuItem>)}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditJob(null)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">Save Updates</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={updateSuccess} autoHideDuration={3000} onClose={() => setUpdateSuccess(false)} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={() => setUpdateSuccess(false)} severity="success" sx={{ width: '100%' }}>Job updated successfully!</Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;
